import Big from 'big.js';
import _ from 'lodash';
import React from 'react';

import {
  AccountDataPoint,
  useEpsAndRevenueTableQuery
} from 'generated/graphql';



export interface IData {
  earningsQoqGrowth: AccountDataPoint[];
  earningsPerShare: AccountDataPoint[];
  earningsYoyGrowth: AccountDataPoint[];
  yearsWithData: number[];
  epsRangeMin: Big;
  epsRangeMax: Big;

  revenue: AccountDataPoint[];
  revenueQoqGrowth: AccountDataPoint[];
  revenueYoyGrowth: AccountDataPoint[];
  revenueRangeMin: Big;
  revenueRangeMax: Big;
}

export function useQuery(id?: string) {
  const tuple = useEpsAndRevenueTableQuery({
    returnPartialData: true,
    skip: !id,
    variables: {
      id: id ?? ''
    }
  });

  return React.useMemo(
    () => {
      const eps = tuple.data?.listedCompany?.standardGlAccounts?.adjdilutedeps?.values ?? [];
      const rev = tuple.data?.listedCompany?.standardGlAccounts?.totalrevenue?.values ?? [];
      const epsData = __getData(eps);
      const revenueData = __getData(rev);

      const data: IData = {
        earningsPerShare: epsData.last4YearsValues,
        earningsQoqGrowth: tuple.data?.listedCompany?.standardGlAccounts?.epsqoqgrowth?.values ?? [],
        earningsYoyGrowth: tuple.data?.listedCompany?.standardGlAccounts?.epsgrowth?.values ?? [],
        epsRangeMin: epsData.rangeMin?.value.gte(0) ? Big(0) : (epsData.rangeMin?.value ?? Big(0)),
        epsRangeMax: epsData.rangeMax?.value ?? Big(0),
        yearsWithData: epsData.last4Years,  // Only take the last 4 years

        revenue: revenueData.last4YearsValues,
        revenueQoqGrowth: [],
        revenueYoyGrowth: tuple.data?.listedCompany?.standardGlAccounts?.revenuegrowth?.values ?? [],
        revenueRangeMin: revenueData.rangeMin?.value.gte(0) ? Big(0) : (revenueData.rangeMin?.value ?? Big(0)),
        revenueRangeMax: revenueData.rangeMax?.value ?? Big(0)
      };

      return {
        ...tuple,
        data
      };
    },
    [tuple]
  );
}



function __getData(dataPoints: AccountDataPoint[]) {
  const allYears = _.uniq(dataPoints.map((datum) => datum.year));
  const sortedYears = _.sortBy(allYears);
  const last4Years = sortedYears.slice(-4);

  const last4YearsValues = dataPoints.filter((d) => last4Years.includes(d.year));
  const quarterlyValues = last4YearsValues.filter((d) => d.period !== 'fy');
  const rangeMin = _.minBy(quarterlyValues, (d) => d.value.toNumber());
  const rangeMax = _.maxBy(quarterlyValues, (d) => d.value.toNumber());

  return {
    last4YearsValues,
    rangeMin,
    rangeMax,
    last4Years
  };
}

